import {DefaultError} from "@tanstack/query-core";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ParseResult} from "papaparse";
import {useCSVReader} from "react-papaparse";
import {useTranslation} from "react-i18next";
import {useMutation, UseMutationOptions} from "@tanstack/react-query";
// eslint-disable-next-line import/no-unresolved
import {Props as CSVReaderProps} from "react-papaparse/dist/useCSVReader";
import {Alert, Box, Divider, LinearProgress, Sheet, SheetProps, Stack, Typography, useTheme} from "@mui/joy";
import pLimit from "p-limit";
import {Pagination} from "@mui/material";
import {isEmpty, unionWith} from "lodash";
import {ActionButton} from "../components/ActionButton.tsx";
import {keyify} from "../util/arutil.tsx";

const DEFAULT_MAX_PROCS = 8;
const DEFAULT_PAGE_SIZE = 10;

type CSVReaderC<T>
    = (props: CSVReaderProps<T>) => React.JSX.Element;

interface UseCSVReaderRT<T> {
    CSVReader: CSVReaderC<T>;
}

interface UpsertFood<T> {
    food: T;
}

export interface BulkUploaderProps<
    TCSV,
    TUpsertFood,
    TUpsertResult
> {
    entityTypeLabel: string | React.JSX.Element,
    headerTransformMap?: Record<string, string>;
    csvToUpsertFood: (csv: TCSV) => TUpsertFood;
    upsertFoodCapsule: (food: TUpsertFood) => React.JSX.Element;
    upsertTqo: UseMutationOptions<TUpsertResult, DefaultError, UpsertFood<TUpsertFood>>;
    existingId: (result: TUpsertResult) => string | null;
    newId: (result: TUpsertResult) => string | null;
    completedCapsule: ({food, id, color, variant}: {
        food: TUpsertFood, id: string,
        color?: SheetProps["color"],
        variant?: SheetProps["variant"],
    }) => React.JSX.Element;
    conflictCapsules: (result: TUpsertResult) => React.JSX.Element[] | null;
}

interface BulkUploaderInternalsProps {
    entityTypeLabel: string | React.JSX.Element;
    getRootProps: () => object;
    acceptedFile?: File;
    ProgressBar: () => React.JSX.Element;
}

function BulkUploaderInternals(props: BulkUploaderInternalsProps) {
    const {t} = useTranslation();
    const theme = useTheme();
    const acceptedFile = props.acceptedFile;
    return (
        <Stack direction="column" spacing={2}>
            <Divider>
                <Typography level="h3" color="primary">{props.entityTypeLabel} {t("admin.csvUpload.title")}</Typography>
            </Divider>
            <Stack direction="row" sx={{px: 2}}>
                <ActionButton sx={{py: 3, px: 8, fontSize: theme.fontSize.xl}}
                              variant="solid" color="primary"
                              {...props.getRootProps()}>
                    {t("admin.csvUpload.browseFile")}
                </ActionButton>
                <Box>
                    {acceptedFile?.name}
                </Box>
            </Stack>
            {acceptedFile ? <>
                <Box>{t("parsingCsv")}</Box>
                <props.ProgressBar/>
            </> : null}
        </Stack>
    );
}

interface BulkUploadCsvReaderProps<TCSV> {
    entityTypeLabel: string | React.JSX.Element;
    headerTransformMap?: Record<string, string>;
    setParseResult: (pr: ParseResult<TCSV> | null) => void;
}

function BulkUploadCsvReader<T>(
    props: BulkUploadCsvReaderProps<T>) {
    const {CSVReader} = useCSVReader() as UseCSVReaderRT<T>;

    return <CSVReader
        config={{
            header: true,
            skipEmptyLines: true,
            transformHeader: (header: string) => {
                return props.headerTransformMap?.[header] ?? header;
            }
        }}
        onUploadAccepted={props.setParseResult}
    >
        {({
              getRootProps,
              ProgressBar,
          }: { getRootProps: () => object, ProgressBar: () => React.JSX.Element }) => {
            return <BulkUploaderInternals entityTypeLabel={props.entityTypeLabel}
                                          getRootProps={getRootProps}
                                          ProgressBar={ProgressBar}/>
        }}
    </CSVReader>
}

type BulkUploadManagerProps<TCSV, TUpsertFood, TUpsertResult> =
    Omit<BulkUploaderProps<TCSV, TUpsertFood, TUpsertResult>,
        "headerTransformMap" | "csvToUpsertFood"> & {
    foods: TUpsertFood[];
    canceler: () => void;
};

// things that depend only on props
interface BulkUploadManagerInitialState {
    ntotal: number;
    isStarted: boolean;
}

// things that depend on stuff in the completed array
interface BulkUploadManagerActiveState {
    canBeStarted: boolean;
    ncompleted: number;
    isFinished: boolean;
    percentComplete: number;
}

interface CompletedUpsert<TFood> {
    food: TFood;
    id: string;
}

interface ConflictedUpsert<TFood> {
    food: TFood;
    conflictCapsules: React.JSX.Element[];
}

interface ErroredUpsert<TFood> {
    food: TFood;
    error: Error;
}

function BulkUploadManager<TCSV, TUpsertFood, TUpsertResult>(
    props: BulkUploadManagerProps<TCSV, TUpsertFood, TUpsertResult>) {
    const foods = props.foods;
    const {t} = useTranslation();
    const [initialState, setInitialState]
        = useState<BulkUploadManagerInitialState>({
        ntotal: foods.length,
        isStarted: false,
    });
    useEffect(() => {
        if (foods.length !== initialState.ntotal) {
            setInitialState((prev) => {
                return {...prev, ntotal: foods.length};
            });
        }
    }, [foods, initialState.ntotal]);
    const [awaitingTrigger, setAwaitingTrigger]
        = useState<TUpsertFood[]>(foods);
    const [awaitingTriggerPage, setAwaitingTriggerPage]
        = useState<number>(1);
    const handleAwaitingTriggerPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setAwaitingTriggerPage(value);
    };
    const [awaitingTriggerPageEntries, setAwaitingTriggerPageEntries]
        = useState<TUpsertFood[]>(foods.slice(0, DEFAULT_PAGE_SIZE));
    useEffect(() => {
        setAwaitingTriggerPageEntries(awaitingTrigger.slice((awaitingTriggerPage - 1) * DEFAULT_PAGE_SIZE,
            awaitingTriggerPage * DEFAULT_PAGE_SIZE));
    }, [awaitingTrigger, awaitingTriggerPage]);
    const [inProgress, setInProgress]
        = useState<TUpsertFood[]>([]);
    const [inProgressPage, setInProgressPage]
        = useState<number>(1);
    const handleInProgressPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setInProgressPage(value);
    };
    const [inProgressPageEntries, setInProgressPageEntries]
        = useState<TUpsertFood[]>([]);
    useEffect(() => {
        setInProgressPageEntries(inProgress.slice((inProgressPage - 1) * DEFAULT_PAGE_SIZE,
            inProgressPage * DEFAULT_PAGE_SIZE));
    }, [inProgress, inProgressPage]);
    const [added, setAdded]
        = useState<CompletedUpsert<TUpsertFood>[]>([]);
    const [addedPage, setAddedPage]
        = useState<number>(1);
    const handleAddedPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setAddedPage(value);
    };
    const [addedPageEntries, setAddedPageEntries]
        = useState<CompletedUpsert<TUpsertFood>[]>([]);
    useEffect(() => {
        setAddedPageEntries(added.slice((addedPage - 1) * DEFAULT_PAGE_SIZE,
            addedPage * DEFAULT_PAGE_SIZE));
    }, [added, addedPage]);
    const [ignored, setIgnored]
        = useState<CompletedUpsert<TUpsertFood>[]>([]);
    const [ignoredPage, setIgnoredPage]
        = useState<number>(1);
    const handleIgnoredPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setIgnoredPage(value);
    };
    const [ignoredPageEntries, setIgnoredPageEntries]
        = useState<CompletedUpsert<TUpsertFood>[]>([]);
    useEffect(() => {
        setIgnoredPageEntries(ignored.slice((ignoredPage - 1) * DEFAULT_PAGE_SIZE,
            ignoredPage * DEFAULT_PAGE_SIZE));
    }, [ignored, ignoredPage]);
    const [conflicted, setConflicted]
        = useState<ConflictedUpsert<TUpsertFood>[]>([]);
    const [conflictedPage, setConflictedPage]
        = useState<number>(1);
    const handleConflictedPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setConflictedPage(value);
    };
    const [conflictedPageEntries, setConflictedPageEntries]
        = useState<ConflictedUpsert<TUpsertFood>[]>([]);
    useEffect(() => {
        setConflictedPageEntries(conflicted.slice((conflictedPage - 1) * DEFAULT_PAGE_SIZE,
            conflictedPage * DEFAULT_PAGE_SIZE));
    }, [conflicted, conflictedPage]);
    const [errored, setErrored]
        = useState<ErroredUpsert<TUpsertFood>[]>([]);
    const [erroredPage, setErroredPage]
        = useState<number>(1);
    const handleErroredPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setErroredPage(value);
    };
    const [erroredPageEntries, setErroredPageEntries]
        = useState<ErroredUpsert<TUpsertFood>[]>([]);
    useEffect(() => {
        setErroredPageEntries(errored.slice((erroredPage - 1) * DEFAULT_PAGE_SIZE,
            erroredPage * DEFAULT_PAGE_SIZE));
    }, [errored, erroredPage]);
    const [activeState, setActiveState]
        = useState<BulkUploadManagerActiveState>({
        canBeStarted: false,
        ncompleted: 0,
        isFinished: false,
        percentComplete: 0,
    });
    const {mutateAsync: performUpload}
        = useMutation({
        ...props.upsertTqo,
        onMutate: (mutargs: UpsertFood<TUpsertFood>) => {
            setAwaitingTrigger(prev => {
                return prev.filter(maybe => maybe !== mutargs.food);
            });
            setInProgress(prev => {
                return unionWith(prev, [mutargs.food]);
            });
        },
        onError: (err, vars) => {
            setInProgress(prev => prev.filter(maybe => maybe !== vars.food));
            setErrored(prev => [...prev, {
                error: err,
                food: vars.food,
            }]);
        },
        onSuccess: (data, vars) => {
            setInProgress(prev => prev.filter(maybe => maybe !== vars.food));
            const newId = props.newId(data);
            if (newId) {
                setAdded(prev => [...prev, {
                    food: vars.food,
                    id: newId,
                }]);
            }
            const existingId = props.existingId(data);
            if (existingId) {
                setIgnored(prev => [...prev, {
                    food: vars.food,
                    id: existingId,
                }]);
            }
            const conflictCapsules = props.conflictCapsules(data);
            if (conflictCapsules && !isEmpty(conflictCapsules)) {
                setConflicted(prev => [...prev, {
                    food: vars.food,
                    conflictCapsules,
                }]);
            }
        }
    })

    const performUploads = useCallback(() => {
        if (activeState.canBeStarted && !initialState.isStarted) {
            setInitialState((prev) => {
                return {...prev, isStarted: true};
            });
            setActiveState((prev) => {
                return {...prev, canBeStarted: false};
            });
            const limit = pLimit(DEFAULT_MAX_PROCS);
            const tasks = awaitingTrigger.map(food => {
                return limit(() => {
                    return performUpload({food});
                });
            });
            setInProgress(prev => [...prev, ...awaitingTrigger]);
            setAwaitingTrigger([]);
            return Promise.all(tasks);
        } else {
            return Promise.reject(new Error("cannot be started"));
        }
    }, [activeState.canBeStarted, awaitingTrigger, initialState.isStarted, performUpload]);

    useEffect(() => {
        const nFoundTriggers = awaitingTrigger.length;
        const nresolved = added.length + ignored.length + errored.length + conflicted.length;
        // we become startable when every record in foods either has
        // been resolved or has a trigger present.
        if (!initialState.isStarted && !activeState.canBeStarted &&
            nFoundTriggers + nresolved === initialState.ntotal) {
            setActiveState((prev) => {
                return {...prev, canBeStarted: true};
            });
        }
        // it is possible that resolutions will come in without triggers
        // actually being fired, so do not rely on nresolved to determine
        // whether we have been officially started.
        // if we get hits in the query cache for all existing records, that
        // could even result in total "completion" without ever needing to
        // hit the go button.
        if (activeState.ncompleted != nresolved) {
            setActiveState((prev) => {
                return {
                    ...prev,
                    ncompleted: nresolved,
                    percentComplete: nresolved / initialState.ntotal * 100,
                    isFinished: nresolved === initialState.ntotal,
                };
            });
        }
    }, [activeState.canBeStarted, activeState.ncompleted, added.length, awaitingTrigger.length, conflicted.length, errored.length, ignored.length, initialState.isStarted, initialState.ntotal]);

    const zoneSxProps = {
        mt: "1rem",
        border: "2px solid",
        borderColor: "primary.400",
        width: "96%",
        margin: "auto",
        alignItems: "center",
        pb: 1,
    };

    return <Stack direction="column" spacing={1.8} sx={{width: "100%", alignItems: "center"}}>
        <Stack direction="row" spacing={2} sx={{width: "80%", py: 1, my: 2}}>
            <ActionButton sx={{flex: 1}}
                          disabled={!activeState.canBeStarted && !activeState.isFinished}
                          perform={() => {
                              props.canceler();
                              return Promise.resolve();
                          }}>
                {t("clear")}
            </ActionButton>

            <ActionButton sx={{flex: 2}}
                          disabled={!activeState.canBeStarted}
                          perform={performUploads}>
                {t("admin.startUpload")}
            </ActionButton>
        </Stack>

        {!activeState.isFinished
            ? <Stack sx={{borderWidth: 4, width: "100%"}} direction="column">
                <Typography level="body-md">{props.entityTypeLabel} {t("admin.uploading")}</Typography>
                <LinearProgress determinate value={activeState.percentComplete}/>
            </Stack>
            : null}

        <Stack direction="column" sx={{
            ...zoneSxProps, display: awaitingTrigger.length > 0
                ? "flex" : "none"
        }}>
            <Sheet color="primary" variant="soft" invertedColors
                   sx={{
                       width: "100%", display: "flex",
                       flexDirection: "row", justifyContent: "center",
                       mb: 1,
                   }}>
                <Typography sx={(theme) => {
                    return {
                        fontSize: theme.vars.fontSize.xl2,
                    };
                }}>{t("admin.upload.awaitingTrigger")}</Typography>
            </Sheet>
            <Pagination page={awaitingTriggerPage} onChange={handleAwaitingTriggerPageChange}
                        color="primary"
                        count={Math.trunc((awaitingTrigger.length - 1) / DEFAULT_PAGE_SIZE) + 1}
                        sx={{
                            display: awaitingTrigger.length > DEFAULT_PAGE_SIZE
                                ? "flex" : "none",
                        }}/>
            <Stack direction="column" sx={{width: "100%", alignItems: "center", mt: 1}} spacing={1}>
                {keyify(awaitingTriggerPageEntries).map(({ix, key, elem}) => {
                    return <Sheet color="primary" variant="soft" key={key}
                                  sx={{width: "95%", borderRadius: "4px"}}>
                        <Stack direction="row" spacing={1.2} sx={{pl: 1.5, py: 1}}>
                            <Box
                                sx={{pl: 1.5}}>{`${((awaitingTriggerPage - 1) * DEFAULT_PAGE_SIZE + ix + 1).toString()}.`}</Box>
                            {props.upsertFoodCapsule(elem)}
                        </Stack>
                    </Sheet>;
                })}
            </Stack>
        </Stack>

        <Stack direction="column" sx={{
            ...zoneSxProps, display: inProgress.length > 0
                ? "flex" : "none"
        }}>
            <Sheet color="primary" variant="soft" invertedColors
                   sx={{
                       width: "100%", display: "flex",
                       flexDirection: "row", justifyContent: "center",
                       mb: 1
                   }}>
                <Typography sx={(theme) => {
                    return {fontSize: theme.vars.fontSize.xl2}
                }}>{t("admin.upload.inProgress")}</Typography>
            </Sheet>
            <Pagination page={inProgressPage} onChange={handleInProgressPageChange}
                        count={Math.trunc((inProgress.length - 1) / DEFAULT_PAGE_SIZE) + 1}
                        sx={{
                            display: inProgress.length > DEFAULT_PAGE_SIZE
                                ? "flex" : "none",
                        }}/>
            <Stack direction="column" sx={{width: "100%", alignItems: "center", mt: 1}} spacing={1}>
                {keyify(inProgressPageEntries).map(({ix, key, elem}) => {
                    return <Alert color="primary" variant="soft" key={key} sx={{width: "95%"}}>
                        {`${((inProgressPage - 1) * DEFAULT_PAGE_SIZE + ix + 1).toString()}.`}
                        {props.upsertFoodCapsule(elem)}
                    </Alert>
                })}
            </Stack>
        </Stack>

        <Stack direction="column" sx={{
            ...zoneSxProps, display: added.length > 0
                ? "flex" : "none"
        }}>
            <Sheet color="success" variant="soft" invertedColors
                   sx={{
                       width: "100%", display: "flex",
                       flexDirection: "row", justifyContent: "center",
                       mb: 1,
                   }}>
                <Typography sx={(theme) => {
                    return {fontSize: theme.vars.fontSize.xl2}
                }}>{t("admin.upload.added")}</Typography>
            </Sheet>
            <Pagination page={addedPage} onChange={handleAddedPageChange}
                        count={Math.trunc((added.length - 1) / DEFAULT_PAGE_SIZE) + 1}
                        sx={{
                            display: added.length > DEFAULT_PAGE_SIZE
                                ? "flex" : "none",
                        }}/>
            <Stack direction="column" sx={{width: "100%", alignItems: "center", mt: 1}} spacing={1}>
                {keyify(addedPageEntries).map(({ix, key, elem}) => {
                    return <Alert color="success" variant="soft" key={key} sx={{width: "95%"}}>
                        {`${((addedPage - 1) * DEFAULT_PAGE_SIZE + ix + 1).toString()}.`}
                        {props.completedCapsule({...elem, color: "success", variant: "soft"})}
                    </Alert>
                })}
            </Stack>
        </Stack>

        <Stack direction="column" sx={{
            ...zoneSxProps, display: ignored.length > 0
                ? "flex" : "none"
        }}>
            <Sheet color="warning" variant="soft" invertedColors
                   sx={{
                       width: "100%", display: "flex",
                       flexDirection: "row", justifyContent: "center",
                       mb: 1,
                   }}>
                <Typography sx={(theme) => {
                    return {fontSize: theme.vars.fontSize.xl2}
                }}>{t("admin.upload.ignored")}</Typography>
            </Sheet>
            <Pagination page={ignoredPage} onChange={handleIgnoredPageChange}
                        count={Math.trunc((ignored.length - 1) / DEFAULT_PAGE_SIZE) + 1}
                        sx={{
                            display: ignored.length > DEFAULT_PAGE_SIZE
                                ? "flex" : "none",
                        }}/>
            <Stack direction="column" sx={{width: "100%", alignItems: "center", mt: 1}} spacing={1}>
                {keyify(ignoredPageEntries).map(({ix, key, elem}) => {
                    return <Alert color="warning" variant="soft" key={key} sx={{width: "95%"}}>
                        {`${((ignoredPage - 1) * DEFAULT_PAGE_SIZE + ix + 1).toString()}.`}
                        {props.completedCapsule({...elem, color: "warning", variant: "soft"})}
                    </Alert>
                })}
            </Stack>
        </Stack>

        <Stack direction="column" sx={{
            ...zoneSxProps, display: conflicted.length > 0
                ? "flex" : "none"
        }}>
            <Sheet color="danger" variant="soft" invertedColors
                   sx={{
                       width: "100%", display: "flex",
                       flexDirection: "row", justifyContent: "center"
                   }}>
                <Typography sx={(theme) => {
                    return {fontSize: theme.vars.fontSize.xl2}
                }}>{t("admin.upload.conflicted")}</Typography>
            </Sheet>
            <Pagination page={conflictedPage} onChange={handleConflictedPageChange}
                        count={Math.trunc((conflicted.length - 1) / DEFAULT_PAGE_SIZE) + 1}
                        sx={{
                            display: conflicted.length > DEFAULT_PAGE_SIZE
                                ? "flex" : "none",
                        }}/>

            <Stack direction="column" sx={{width: "100%", alignItems: "center", mt: 1}} spacing={1}>
                {keyify(conflictedPageEntries).map(({ix, key, elem}) => {
                    return <Alert color="danger" variant="solid" sx={{width: "85%"}} key={key}>
                        <Stack direction="row">
                            {`${((conflictedPage - 1) * DEFAULT_PAGE_SIZE + ix + 1).toString()}.`}
                            {props.upsertFoodCapsule(elem.food)}
                            <Typography level="body-md">{t("admin.upload.conflicted")}</Typography>
                            {...elem.conflictCapsules}
                        </Stack>
                    </Alert>
                })}
            </Stack>
        </Stack>

        <Stack direction="column" sx={{
            ...zoneSxProps, display: errored.length > 0
                ? "flex" : "none"
        }}>
            <Sheet color="danger" variant="solid" invertedColors
                   sx={{
                       width: "100%", display: "flex",
                       flexDirection: "row", justifyContent: "center"
                   }}>
                <Typography sx={(theme) => {
                    return {fontSize: theme.vars.fontSize.xl2}
                }}>{t("admin.upload.errored")}</Typography>
            </Sheet>
            <Pagination page={erroredPage} onChange={handleErroredPageChange}
                        count={Math.trunc((errored.length - 1) / DEFAULT_PAGE_SIZE) + 1}
                        sx={{
                            display: errored.length > DEFAULT_PAGE_SIZE
                                ? "flex" : "none",
                        }}/>

            {keyify(erroredPageEntries).map(({ix, key, elem}) => {
                return <Alert color="danger" variant="solid" key={key} sx={{width: "95%"}}>
                    <Stack direction="row">
                        {`${((erroredPage - 1) * DEFAULT_PAGE_SIZE + ix + 1).toString()}.`}
                        {props.upsertFoodCapsule(elem.food)}
                        <Typography level="body-md">{t("admin.upload.errored")}</Typography>
                        {elem.error.toString()}
                    </Stack>
                </Alert>
            })}
        </Stack>
    </Stack>
}

export const BulkUploader = <
    TCSV,
    TUpsertFood,
    TUpsertResult
>
(props: BulkUploaderProps<TCSV, TUpsertFood, TUpsertResult>) => {
    const [csvParseResult, setCsvParseResult]
        = useState<ParseResult<TCSV> | null>(null);
    const canceler = () => {
        setCsvParseResult(null);
    };
    const sanitizedCsv = useMemo(() => {
        if (csvParseResult) {
            return csvParseResult.data.map((csv) => {
                const rv = props.csvToUpsertFood(csv);
                return rv;
            });
        }
        return null;
    }, [csvParseResult, props]);

    if (!csvParseResult) {
        return <BulkUploadCsvReader setParseResult={setCsvParseResult}
                                    entityTypeLabel={props.entityTypeLabel}
                                    headerTransformMap={props.headerTransformMap}
        />;
    }

    if (sanitizedCsv) {
        return <BulkUploadManager<TCSV, TUpsertFood, TUpsertResult>
            entityTypeLabel={props.entityTypeLabel}
            foods={sanitizedCsv} canceler={canceler}
            upsertTqo={props.upsertTqo}
            completedCapsule={props.completedCapsule}
            conflictCapsules={props.conflictCapsules}
            upsertFoodCapsule={props.upsertFoodCapsule}
            existingId={props.existingId}
            newId={props.newId}
        />
    }

    return null;
};
